/**
 * Standalone Page - Generic page for menus that don't require a project
 * Used for HSE, Training, Processes, Development, Download App
 * 
 * Sub-menu items are shown as flyouts in the main ProjectSidebar (hover only).
 * This component only renders the content area.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { getMyFeatureFlags, isFlagAdmin } from '../../utils/featureFlags';
import DownloadApp from '../DownloadApp';

const PortalOverviewDownloadButton = () => {
  const { instance, accounts } = useMsal();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'get', '/download/portal-overview', null, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LGL_PORTAL_COMPLETE_OVERVIEW.md';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      style={{
        padding: '12px 28px',
        backgroundColor: downloading ? '#7fb3e0' : '#4a90d9',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: downloading ? 'not-allowed' : 'pointer',
        fontSize: '15px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'background-color 0.15s ease'
      }}
      onMouseEnter={e => { if (!downloading) e.currentTarget.style.backgroundColor = '#357abd'; }}
      onMouseLeave={e => { if (!downloading) e.currentTarget.style.backgroundColor = '#4a90d9'; }}
    >
      {downloading ? 'Downloading...' : '⬇ Download File'}
    </button>
  );
};

const StandalonePage = ({ 
  title, 
  icon, 
  menuItems = [], 
  renderContent 
}) => {
  const location = useLocation();
  // Email deep-links use ?menu=… (React Router state is lost on a fresh URL open).
  // Sidebar navigation still passes location.state.menuItem.
  const menuFromQuery = (() => {
    try {
      return new URLSearchParams(location.search).get('menu');
    } catch {
      return null;
    }
  })();
  const activeMenuItem = location.state?.menuItem
    || menuFromQuery
    || (menuItems.length > 0 ? menuItems[0]?.id : null);

  const defaultContent = (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
      <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
      <p>This feature is under development.</p>
    </div>
  );

  // No internal sub-menu sidebar - content only
  // Sub-menu items are shown as flyouts in the main ProjectSidebar
  return (
    <div key={activeMenuItem} style={{ height: '100%', backgroundColor: '#f8f9fa', overflowY: 'auto' }}>
      {renderContent ? renderContent(activeMenuItem) : defaultContent}
    </div>
  );
};

// HSE Page
export const HSEPage = () => {
  const { instance, accounts } = useMsal();
  const SafetyTrainingPage = React.lazy(() => import('../Safety/SafetyTrainingPage'));

  // Staged rollout: Training DB + unfinished HSE forms are gated by feature
  // flags (PMO > Development > Feature Flags). Hidden until flags load.
  const [featureFlags, setFeatureFlags] = useState(null);
  useEffect(() => {
    if (accounts[0]) getMyFeatureFlags(instance, accounts).then(setFeatureFlags);
  }, [instance, accounts]);
  const flagOn = (key) => !!featureFlags && featureFlags[key] !== false;

  const HSEReport = React.lazy(() => import('../Reports/HSEReport'));

  const HSE_WIP_ITEMS = [
    { id: 'safety-jha-jsa', label: 'Job Hazard Analysis (JHA/JSA)' },
    { id: 'safety-toolbox-talk', label: 'Toolbox Safety Talk' },
    { id: 'safety-daily-inspection', label: 'Daily Site Safety Inspection' },
    { id: 'safety-ptw-piping-mechanical', label: 'Permit to Work (PTW)' },
    { id: 'safety-hot-work-permit', label: 'Hot Work Permit' },
    { id: 'safety-incident-near-miss', label: 'Incident / Near-Miss Report' },
    { id: 'safety-certificates-db', label: 'Safety Certificates DB' },
  ];

  const menuItems = [
    { id: 'hse-report', label: 'HSE Report' },
    ...(flagOn('safety-training-db') ? [{ id: 'safety-training-db', label: 'Training Database' }] : []),
    ...HSE_WIP_ITEMS.filter((item) => flagOn(item.id)),
  ];

  const renderContent = (activeMenuItem) => {
    if (activeMenuItem === 'hse-report' || activeMenuItem === 'safety') {
      return (
        <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>}>
          <HSEReport />
        </React.Suspense>
      );
    }
    if (flagOn('safety-training-db') && activeMenuItem === 'safety-training-db') {
      return (
        <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>}>
          <SafetyTrainingPage />
        </React.Suspense>
      );
    }
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
        <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
        <p>This feature is under development.</p>
      </div>
    );
  };

  return <StandalonePage title="HSE" icon="🦺" menuItems={menuItems} renderContent={renderContent} />;
};

// Training Page — all leaves are Coming Soon stubs; gated by feature flags (pilot = builder).
export const TrainingPage = () => {
  const { instance, accounts } = useMsal();
  const [featureFlags, setFeatureFlags] = useState(null);
  useEffect(() => {
    if (accounts[0]) getMyFeatureFlags(instance, accounts).then(setFeatureFlags);
  }, [instance, accounts]);
  const flagOn = (key) => !!featureFlags && featureFlags[key] !== false;

  const menuItems = [
    { id: 'training-lgl-portal', label: 'LGL Portal' },
    { id: 'training-sharepoint-naming', label: 'Naming Conventions' },
    { id: 'training-sharepoint-templates', label: 'Finding Templates' },
    { id: 'training-build', label: 'Build' },
    { id: 'training-shortcuts', label: 'Keyboard Shortcuts' },
    { id: 'training-mouse-gestures', label: 'Mouse & Trackpad Tips' },
    { id: 'training-question-to-admin', label: 'Question to Admin' },
  ].filter((item) => flagOn(item.id));
  return <StandalonePage title="Training" icon="🎓" menuItems={menuItems} />;
};

// Processes Page — tour + suggestion box are stubs (flagged); flowcharts are live.
export const ProcessesPage = () => {
  const { instance, accounts } = useMsal();
  const [featureFlags, setFeatureFlags] = useState(null);
  useEffect(() => {
    if (accounts[0]) getMyFeatureFlags(instance, accounts).then(setFeatureFlags);
  }, [instance, accounts]);
  const flagOn = (key) => !!featureFlags && featureFlags[key] !== false;

  // Dynamically import ProcessFlowchart to avoid circular dependencies
  const ProcessFlowchart = React.lazy(() => import('../Processes/ProcessFlowchart'));
  
  const menuItems = [
    { id: 'lgl-portal-tour', label: 'LGL Portal Tour' },
    { id: 'processes-permissions-guide', label: 'Permissions Guide (PDF)' },
    { id: 'processes-requisitions', label: 'Material Requisition' },
    { id: 'processes-purchasing', label: 'Purchasing' },
    { id: 'processes-estimation', label: 'Estimation' },
    { id: 'processes-scheduling', label: 'Scheduling' },
    { id: 'processes-suggestion-box', label: 'Suggestion Box' },
  ].filter((item) => {
    if (item.id === 'lgl-portal-tour' || item.id === 'processes-suggestion-box') return flagOn(item.id);
    return true;
  });
  
  const renderContent = (activeMenuItem) => {
    // Permissions guide: static PDF served from public/docs
    if (activeMenuItem === 'processes-permissions-guide') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 170px)', minHeight: 480 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12, flexWrap: 'wrap', padding: '10px 4px 12px',
          }}>
            <div style={{ fontSize: 13, color: '#6c757d' }}>
              How access and permissions work, in plain English — written for managers and superadmins.
              The always-current version lives in <strong>Admin Panel → Permissions → How It Works</strong>.
            </div>
            <a
              href="/docs/permissions-guide.pdf"
              download="LGL Portal - Permissions Guide.pdf"
              style={{
                padding: '8px 16px', backgroundColor: '#007bff', color: '#fff', borderRadius: 6,
                fontSize: 13, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              ⬇ Download PDF
            </a>
          </div>
          <iframe
            title="Permissions Guide"
            src="/docs/permissions-guide.pdf"
            style={{ flex: 1, width: '100%', border: '1px solid #e9ecef', borderRadius: 8, backgroundColor: '#fff' }}
          />
        </div>
      );
    }

    // Show flowchart for process items
    if (activeMenuItem?.startsWith('processes-')) {
      return (
        <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>}>
          <ProcessFlowchart processId={activeMenuItem} />
        </React.Suspense>
      );
    }
    
    // Default content for other items
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
        <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
        <p>This feature is under development.</p>
      </div>
    );
  };
  
  return <StandalonePage title="Processes" icon="🔀" menuItems={menuItems} renderContent={renderContent} />;
};

// Development Page
export const DevelopmentPage = () => {
  const { accounts } = useMsal();
  const DevelopmentLog = React.lazy(() => import('../Development/DevelopmentLog'));
  const CodeRefactoringDashboard = React.lazy(() => import('../Development/CodeRefactoringDashboard'));
  const DashboardTemplate = React.lazy(() => import('../Development/DashboardTemplate'));
  const DashboardFeaturesMatrix = React.lazy(() => import('../Development/DashboardFeaturesMatrix'));
  const PermissionsDashboard = React.lazy(() => import('../Development/PermissionsDashboard'));
  const FilteringTemplatePage = React.lazy(() => import('../Development/FilteringTemplatePage'));
  const ViewingTemplatePage = React.lazy(() => import('../Development/ViewingTemplatePage'));
  const SortingTemplatePage = React.lazy(() => import('../Development/SortingTemplatePage'));
  const AutofitTemplatePage = React.lazy(() => import('../Development/AutofitTemplatePage'));
  const AuditLogTemplatePage = React.lazy(() => import('../Development/AuditLogTemplatePage'));
  const ColumnConfigTemplatePage = React.lazy(() => import('../Development/ColumnConfigTemplatePage'));
  const HelpButtonTemplatePage = React.lazy(() => import('../Development/HelpButtonTemplatePage'));
  const SearchBoxTemplatePage = React.lazy(() => import('../Development/SearchBoxTemplatePage'));
  const ColumnResizeTemplatePage = React.lazy(() => import('../Development/ColumnResizeTemplatePage'));
  const FillDownTemplatePage = React.lazy(() => import('../Development/FillDownTemplatePage'));
  const ProposalsDashboard = React.lazy(() => import('../Development/ProposalsDashboard'));
  const DebuggingPage = React.lazy(() => import('../Development/DebuggingPage'));
  const FileUploadPage = React.lazy(() => import('../Development/FileUploadPage'));
  const NormalDistributionPage = React.lazy(() => import('../Development/NormalDistributionPage'));
  const EmailTestPage = React.lazy(() => import('../Development/EmailTestPage'));
  const ChatbotAuditDashboard = React.lazy(() => import('../Admin/ChatbotAuditDashboard'));
  const UniverPrototypePage = React.lazy(() => import('../Development/UniverPrototypePage'));
  const HandsontablePrototypePage = React.lazy(() => import('../Development/HandsontablePrototypePage'));
  const JspreadsheetPrototypePage = React.lazy(() => import('../Development/JspreadsheetPrototypePage'));
  const DatabaseSchemaPage = React.lazy(() => import('../Development/DatabaseSchemaPage'));
  const RoadmapPage = React.lazy(() => import('../Development/RoadmapPage'));
  const RoadmapSwimLanePage = React.lazy(() => import('../Development/RoadmapSwimLanePage'));
  const RoadmapStrategyPage = React.lazy(() => import('../Development/RoadmapStrategyPage'));
  const EmployeeManagementPage = React.lazy(() => import('../Development/EmployeeManagementPage'));
  const EmailLogPage = React.lazy(() => import('../Development/EmailLogPage'));
  const NotificationRecipientsPage = React.lazy(() => import('../Development/NotificationRecipientsPage'));
  const InfraHealthDashboard = React.lazy(() => import('../Development/InfraHealthDashboard'));
  const FeatureFlagsPage = React.lazy(() => import('../Development/FeatureFlagsPage'));
  const PortalUsageDashboard = React.lazy(() => import('../PMO/PortalUsageDashboard'));
  
  const menuItems = [
    { id: 'development-proposals', label: 'Proposals' },
    { id: 'development-log', label: 'Development Log' },
    // Feature Flags page is builder-only (not all superadmins) — API enforces too
    ...(isFlagAdmin(accounts[0]?.username) ? [{ id: 'development-feature-flags', label: 'Feature Flags' }] : []),
    { id: 'development-portal-overview', label: 'Portal Overview Doc' },
    { id: 'development-debugging', label: 'Debugging' },
    { id: 'code-refactoring', label: 'Code Refactoring' },
    { id: 'dashboard-features-matrix', label: 'Features Matrix' },
    { id: 'dashboard-permissions', label: 'Permissions Dashboard' },
    { id: 'dashboard-infra-health', label: 'Infra Health' },
    { id: 'template-dashboard', label: 'Dashboard Template' },
    { id: 'modules-filtering-template', label: 'Filtering Template' },
    { id: 'modules-viewing-template', label: 'Viewing Template' },
    { id: 'modules-sorting-template', label: 'Sorting Template' },
    { id: 'modules-autofit-template', label: 'Autofit Template' },
    { id: 'modules-audit-log-template', label: 'Audit Log Template' },
    { id: 'modules-column-config-template', label: 'Column Config Template' },
    { id: 'modules-help-button-template', label: 'Help Button Template' },
    { id: 'modules-dashboard-template', label: 'Dashboard Template' },
    { id: 'modules-search-box-template', label: 'Search Box Template' },
    { id: 'modules-column-resize-template', label: 'Column Resize Template' },
    { id: 'file-upload', label: 'File Upload' },
    { id: 'pmo-usage', label: 'Portal Usage' },
    { id: 'roadmap-erp', label: 'ERP Roadmap' },
    { id: 'roadmap-swimlane', label: 'Timeline' },
    { id: 'roadmap-strategy', label: 'Strategy' },
    { id: 'wip-database-schema', label: 'Database Schema' },
    { id: 'wip-chatbot-audit', label: 'Chatbot Audit Log' },
    { id: 'wip-normal-distribution', label: 'Normal Distribution' },
    { id: 'wip-spreadsheet-univer', label: 'Spreadsheet: Univer' },
    { id: 'wip-spreadsheet-handsontable', label: 'Spreadsheet: Handsontable' },
    { id: 'wip-spreadsheet-jspreadsheet', label: 'Spreadsheet: jspreadsheet' },
    { id: 'wip-employee-management', label: 'Employee Management Process' },
    { id: 'test-email-reminders', label: 'Email Reminder Tests' },
    { id: 'test-piping-progress-tracker', label: 'Piping Progress Tracker' },
    { id: 'test-mech-rot-eqp-tracker', label: 'Mech/Rot Eqp Tracker' },
  ];
  
  const renderContent = (activeMenuItem) => {
    const loadingFallback = <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
    
    switch (activeMenuItem) {
      case 'development-proposals':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ProposalsDashboard />
          </React.Suspense>
        );
      case 'development-log':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DevelopmentLog />
          </React.Suspense>
        );
      case 'development-feature-flags':
        return (
          <React.Suspense fallback={loadingFallback}>
            <FeatureFlagsPage />
          </React.Suspense>
        );
      case 'development-portal-overview':
        return (
          <div style={{ padding: '48px', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', color: '#2c3e50' }}>
              <span style={{ fontSize: '32px' }}>📋</span>
              <span>Portal Overview Document</span>
            </h1>
            <p style={{ color: '#6c757d', marginBottom: '32px', fontSize: '15px' }}>
              A comprehensive overview of the entire LGL Portal — all modules, features, permissions, architecture, and workflows. 
              Upload this to NotebookLM to generate an audio overview, or use it as a reference document.
            </p>
            <div style={{
              backgroundColor: 'white',
              padding: '32px',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '12px',
                  backgroundColor: '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '28px'
                }}>📄</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '16px', color: '#2c3e50' }}>LGL_PORTAL_COMPLETE_OVERVIEW.md</div>
                  <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>Markdown Document &middot; 31 sections &middot; ~4,000 words</div>
                </div>
              </div>
              <div style={{ fontSize: '14px', color: '#495057', marginBottom: '24px', lineHeight: 1.6 }}>
                <strong>Sections include:</strong> System overview, authentication, 3-layer permission system, progress tracking (8 disciplines), 
                time tracker, schedule management, forecasting &amp; EVM, reports, change management, material management, 
                estimation (31-sheet cost breakdown), technical module, RFI log, resource management, document management, 
                QA/QC, HSE, sales, procurement, support tickets, AI chatbot, admin panel, real-time collaboration, 
                background jobs, database architecture, deployment, and a full glossary.
              </div>
              <PortalOverviewDownloadButton />
            </div>
          </div>
        );
      case 'development-debugging':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DebuggingPage />
          </React.Suspense>
        );
      case 'code-refactoring':
        return (
          <React.Suspense fallback={loadingFallback}>
            <CodeRefactoringDashboard />
          </React.Suspense>
        );
      case 'dashboard-features-matrix':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DashboardFeaturesMatrix />
          </React.Suspense>
        );
      case 'dashboard-permissions':
        return (
          <React.Suspense fallback={loadingFallback}>
            <PermissionsDashboard />
          </React.Suspense>
        );
      case 'dashboard-email-log':
        return (
          <React.Suspense fallback={loadingFallback}>
            <EmailLogPage />
          </React.Suspense>
        );
      case 'dashboard-notification-recipients':
        return (
          <React.Suspense fallback={loadingFallback}>
            <NotificationRecipientsPage />
          </React.Suspense>
        );
      case 'dashboard-infra-health':
        return (
          <React.Suspense fallback={loadingFallback}>
            <InfraHealthDashboard />
          </React.Suspense>
        );
      case 'template-dashboard':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DashboardTemplate />
          </React.Suspense>
        );
      case 'modules-filtering-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <FilteringTemplatePage />
          </React.Suspense>
        );
      case 'modules-viewing-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ViewingTemplatePage />
          </React.Suspense>
        );
      case 'modules-sorting-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <SortingTemplatePage />
          </React.Suspense>
        );
      case 'modules-autofit-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <AutofitTemplatePage />
          </React.Suspense>
        );
      case 'modules-audit-log-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <AuditLogTemplatePage />
          </React.Suspense>
        );
      case 'modules-column-config-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ColumnConfigTemplatePage />
          </React.Suspense>
        );
      case 'modules-help-button-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <HelpButtonTemplatePage />
          </React.Suspense>
        );
      case 'modules-dashboard-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DashboardTemplate />
          </React.Suspense>
        );
      case 'modules-search-box-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <SearchBoxTemplatePage />
          </React.Suspense>
        );
      case 'modules-column-resize-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ColumnResizeTemplatePage />
          </React.Suspense>
        );
      case 'modules-fill-down-template':
        return (
          <React.Suspense fallback={loadingFallback}>
            <FillDownTemplatePage />
          </React.Suspense>
        );
      case 'file-upload':
        return (
          <React.Suspense fallback={loadingFallback}>
            <FileUploadPage />
          </React.Suspense>
        );
      case 'pmo-usage':
        return (
          <React.Suspense fallback={loadingFallback}>
            <PortalUsageDashboard />
          </React.Suspense>
        );
      case 'roadmap-erp':
        return (
          <React.Suspense fallback={loadingFallback}>
            <RoadmapPage />
          </React.Suspense>
        );
      case 'roadmap-swimlane':
        return (
          <React.Suspense fallback={loadingFallback}>
            <RoadmapSwimLanePage />
          </React.Suspense>
        );
      case 'roadmap-strategy':
        return (
          <React.Suspense fallback={loadingFallback}>
            <RoadmapStrategyPage />
          </React.Suspense>
        );
      case 'wip-database-schema':
        return (
          <React.Suspense fallback={loadingFallback}>
            <DatabaseSchemaPage />
          </React.Suspense>
        );
      case 'wip-chatbot-audit':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ChatbotAuditDashboard standalone />
          </React.Suspense>
        );
      case 'wip-normal-distribution':
        return (
          <React.Suspense fallback={loadingFallback}>
            <NormalDistributionPage />
          </React.Suspense>
        );
      case 'wip-spreadsheet-univer':
        return (
          <React.Suspense fallback={loadingFallback}>
            <UniverPrototypePage />
          </React.Suspense>
        );
      case 'wip-spreadsheet-handsontable':
        return (
          <React.Suspense fallback={loadingFallback}>
            <HandsontablePrototypePage />
          </React.Suspense>
        );
      case 'wip-spreadsheet-jspreadsheet':
        return (
          <React.Suspense fallback={loadingFallback}>
            <JspreadsheetPrototypePage />
          </React.Suspense>
        );
      case 'wip-employee-management':
        return (
          <React.Suspense fallback={loadingFallback}>
            <EmployeeManagementPage />
          </React.Suspense>
        );
      case 'test-email-reminders':
        return (
          <React.Suspense fallback={loadingFallback}>
            <EmailTestPage />
          </React.Suspense>
        );
      default:
        return (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
            <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
            <p>This feature is under development.</p>
          </div>
        );
    }
  };
  
  return <StandalonePage title="Development" icon="🛠️" menuItems={menuItems} renderContent={renderContent} />;
};

// Download App Page
export const DownloadAppPage = () => <DownloadApp />;

// Sales Page
export const SalesPage = () => {
  const ClientsDatabasePage = React.lazy(() => import('../Sales/ClientsDatabasePage'));
  const BidLogPage = React.lazy(() => import('../Sales/BidLogPage'));
  
  const menuItems = [
    { id: 'clients-database', label: 'Clients Database' },
    { id: 'bid-log', label: 'Bid Log' },
  ];
  
  const renderContent = (activeMenuItem) => {
    const loadingFallback = <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
    
    switch (activeMenuItem) {
      case 'clients-database':
        return (
          <React.Suspense fallback={loadingFallback}>
            <ClientsDatabasePage />
          </React.Suspense>
        );
      case 'bid-log':
        return (
          <React.Suspense fallback={loadingFallback}>
            <BidLogPage />
          </React.Suspense>
        );
      default:
        return (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
            <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
            <p>This feature is under development.</p>
          </div>
        );
    }
  };
  
  return <StandalonePage title="Sales" icon="💼" menuItems={menuItems} renderContent={renderContent} />;
};

export default StandalonePage;
